import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Plus, ArrowRight, Trash, X, MapPin } from "@phosphor-icons/react";

export default function BaremosTab({ aseguradoraId, canEdit = true }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", descripcion: "", provincias: "" });
  const [err, setErr] = useState("");

  const load = async () => {
    const r = await api.get(`/baremos?aseguradora_id=${aseguradoraId}`);
    setItems(r.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [aseguradoraId]);

  const create = async () => {
    setErr("");
    try {
      const provs = (form.provincias || "")
        .split(",").map((s) => s.trim()).filter(Boolean);
      await api.post("/baremos", {
        aseguradora_id: aseguradoraId,
        name: form.name,
        descripcion: form.descripcion || null,
        provincias: provs,
      });
      setOpen(false);
      setForm({ name: "", descripcion: "", provincias: "" });
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error");
    }
  };

  const del = async (e, b) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm(`¿Eliminar baremo "${b.name}" y todas sus líneas?`)) return;
    await api.delete(`/baremos/${b.id}`);
    load();
  };

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display font-bold text-base tracking-tight">Tarifas / Baremos</h2>
          <p className="text-xs text-muted">
            Crea uno o varios baremos. Útil para diferenciar por provincias o modalidad
            (presencial vs videoperitación).
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setOpen(true)}
            data-testid="new-baremo-button"
            className="bg-foreground text-white text-xs px-3 py-1.5 rounded-md font-medium inline-flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" weight="bold" /> Nuevo baremo
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-10 text-center text-muted bg-white">
          <div className="font-display font-bold text-base mb-1 tracking-tight text-foreground">
            Sin baremos
          </div>
          <p className="text-xs">Crea el primero. Puede ser un baremo general o específico de provincias / modalidad.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((b) => (
            <Link
              key={b.id}
              to={`/admin/aseguradoras/${aseguradoraId}/baremos/${b.id}`}
              data-testid={`baremo-card-${b.id}`}
              className="border border-border rounded-md bg-white p-4 transition-all relative group hover:border-foreground hover:shadow-sm hover:-translate-y-0.5 hover:bg-surface/50"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-sm tracking-tight truncate">{b.name}</div>
                  {b.descripcion && (
                    <div className="text-[11px] text-muted mt-0.5 line-clamp-2">{b.descripcion}</div>
                  )}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {(b.provincias || []).length === 0 ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-surface text-muted font-medium">Todas las provincias</span>
                ) : (
                  b.provincias.slice(0, 4).map((p) => (
                    <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-surface text-muted font-medium inline-flex items-center gap-1">
                      <MapPin className="w-2.5 h-2.5" weight="bold" /> {p}
                    </span>
                  ))
                )}
                {(b.provincias || []).length > 4 && (
                  <span className="text-[10px] text-muted">+{b.provincias.length - 4}</span>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-border/60 flex items-center justify-between">
                <span className="text-[11px] text-muted group-hover:text-foreground inline-flex items-center gap-1">
                  Editar líneas <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" weight="bold" />
                </span>
                <button onClick={(e) => del(e, b)} className={`opacity-0 group-hover:opacity-100 p-1 text-muted hover:text-status-error-fg ${canEdit ? "" : "hidden"}`} data-testid={`del-baremo-${b.id}`}>
                  <Trash className="w-3 h-3" weight="bold" />
                </button>
              </div>
            </Link>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-lg w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg tracking-tight">Nuevo baremo</h2>
              <button onClick={() => setOpen(false)} className="text-muted"><X className="w-4 h-4" weight="bold" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label>Nombre</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="baremo-name"
                  placeholder="ej. Baremo general 2026"
                />
              </div>
              <div>
                <label>Descripción (opcional)</label>
                <input
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  data-testid="baremo-descripcion"
                  placeholder="ej. Tarifa videoperitación nacional"
                />
              </div>
              <div>
                <label>Provincias (opcional)</label>
                <input
                  value={form.provincias}
                  onChange={(e) => setForm({ ...form, provincias: e.target.value })}
                  data-testid="baremo-provincias"
                  placeholder="Bizkaia, Madrid, Salamanca..."
                />
                <div className="text-[10px] text-muted mt-1 normal-case tracking-normal">
                  Separadas por comas. Vacío = aplica a todas las provincias.
                </div>
              </div>
              {err && <div className="text-xs text-status-error-fg bg-status-error-bg border border-status-error-bd px-2 py-1.5 rounded">{err}</div>}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="text-xs px-4 py-2 text-muted">Cancelar</button>
              <button
                onClick={create}
                disabled={!form.name.trim()}
                data-testid="save-baremo"
                className="bg-foreground text-white text-xs px-4 py-2 rounded-md font-medium disabled:opacity-50"
              >Crear</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
